import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { setCopyMetadata } from '@disco/state-management/actions/copy-metadata-actions'
import { getLocationBeforeTransitions } from '@disco/state-management/selectors/routing'

import type { TrackId } from 'types/track'

import { getShortlistBaseRoute } from 'utils/routing'

import Button from 'components/ui/button'
import EllipsisMenu from 'components/ui/ellipsis-menu'

import { useDispatch, useSelector } from 'hooks/use-redux'

type Props = {
  trackIds: TrackId[]
  variant?: 'default' | 'inline'
}

const InlineCopyMetadataButton = ({ trackIds }: { trackIds: TrackId[] }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useSelector(getLocationBeforeTransitions)

  const getCopyMetadataRoute = useCallback(() => {
    const baseRoute = getShortlistBaseRoute(location)
    return baseRoute ? `${baseRoute}/copy-metadata` : '/explorer/copy-metadata'
  }, [location])

  const setCopyMetadataTarget = useCallback(() => {
    dispatch(
      setCopyMetadata({
        targetTrackIds: trackIds,
      }),
    )
    navigate(getCopyMetadataRoute())
    dispatch(sendMixpanelEvent('Copy track info to track (modal)'))
  }, [dispatch, trackIds, getCopyMetadataRoute, navigate])

  return (
    <Button
      appearance="inline-link"
      caption={
        trackIds.length > 1
          ? 'Copy track info to these tracks'
          : 'Copy track info to this track'
      }
      onClick={setCopyMetadataTarget}
    />
  )
}

const DefaultCopyMetadataButton = ({ trackIds }: { trackIds: TrackId[] }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useSelector(getLocationBeforeTransitions)

  const getCopyMetadataRoute = useCallback(() => {
    const baseRoute = getShortlistBaseRoute(location)
    return baseRoute ? `${baseRoute}/copy-metadata` : '/explorer/copy-metadata'
  }, [location])

  const setCopyMetadataSource = useCallback(() => {
    dispatch(
      setCopyMetadata({
        sourceTrackId: trackIds[0],
      }),
    )
    navigate(getCopyMetadataRoute())
    dispatch(sendMixpanelEvent('Copy track info from track (modal)'))
  }, [dispatch, trackIds, getCopyMetadataRoute, navigate])
  const setCopyMetadataTarget = useCallback(() => {
    dispatch(
      setCopyMetadata({
        targetTrackIds: trackIds,
      }),
    )
    navigate(getCopyMetadataRoute())
    dispatch(sendMixpanelEvent('Copy track info to track (modal)'))
  }, [dispatch, trackIds, getCopyMetadataRoute, navigate])
  const previewMetadata = useCallback(() => {
    if (trackIds[0]) {
      const baseRoute = getShortlistBaseRoute(location)
      navigate(
        baseRoute
          ? `${baseRoute}/tracks/${trackIds[0]}/preview-metadata`
          : `/explorer/tracks/${trackIds[0]}/preview-metadata`,
      )
      dispatch(sendMixpanelEvent('Preview track info (modal)'))
    }
  }, [dispatch, trackIds, navigate, location])
  const options = useMemo(
    () => [
      {
        label: 'Copy track info to this track',
        onClick: setCopyMetadataTarget,
      },
      {
        label: 'Copy track info from this track',
        onClick: setCopyMetadataSource,
      },
      {
        label: 'Preview track info',
        onClick: previewMetadata,
      },
    ],
    [setCopyMetadataTarget, setCopyMetadataSource, previewMetadata],
  )

  return trackIds.length > 1 ? (
    <Button
      caption="Copy track info to these tracks"
      onClick={setCopyMetadataTarget}
    />
  ) : (
    <EllipsisMenu compact caption="Copy track info" options={options} />
  )
}

const CopyMetadataButton = ({ trackIds, variant = 'default' }: Props) => {
  if (variant === 'inline') {
    return <InlineCopyMetadataButton trackIds={trackIds} />
  }
  return <DefaultCopyMetadataButton trackIds={trackIds} />
}

export default React.memo<Props>(CopyMetadataButton)
